<script setup>
  import {computed, defineEmits, onMounted, ref, watch} from "vue";
  import {$dateFormatterWithDay,} from "@/mixins/Helpers";
  import {useStore} from "vuex";
  import {useLocales} from "@/composables/locales";
  import FileUploaded from "@/components/FileUploaded";
  import {useRoute, useRouter} from "vue-router";
  import {useSwal} from "@/composables/useSwal";
  import { useI18n } from 'vue-i18n'
  import mailConst from "@/composables/mailConst";
  import {mailActions} from "@/Modules/Mail/views/Mail/composables/mailActions";

  const router = useRouter()
  const route =  useRoute()
  const {showSwal} = useSwal()
  const { t } = useI18n({ useScope: 'global' })

  const emits = defineEmits(['handleAction'])


  const {locale} = useLocales()
  const store = useStore()
  const attachFiles = ref([])

  const props = defineProps({
    user: {
      type: Object,
      default: {}
    },
    large: {
      type: Boolean,
      default: true
    },
    schedule: {
      type: Boolean,
      default: false
    },
    isDetailLoading: {
      type: Boolean,
      default: false
    }
  })

  const disabled = ref(false)

  const currentMail = computed(() => store.getters['mails/getCurrentMail'])
  const currentUser = computed(() => store.getters['user/currentUser'])
  const folder = computed(() => decodeURIComponent(decodeURIComponent(route.query.tab)))
  const draftFolder = computed(() => route.query.tab && decodeURIComponent(route.query.tab) == mailConst.DRAFT)
  const inboxFolder = computed(() => route.query.tab && decodeURIComponent(route.query.tab) == mailConst.INBOX)
  const sendFolder = computed(() => route.query.tab && decodeURIComponent(route.query.tab) == mailConst.SEND)
  const imapConfig = computed(() => store.getters['mails/getImapConfig'])


  const fromLabel = computed(() => {
    const mail = props.currentMail

    if (mail.from_name && !mail.from) return mail.from_name

    if (mail.from) {
      const personal = mail.from.personal || ""
      const address = mail.from.mail || ""

      if (personal && address) return `${personal}, ${address}`
      if (personal) return personal
      if (address) return address
    }

    return ""
  })

  const currentMailAttachmentToString = computed(() => JSON.stringify(currentMail.value.attachments || []) )


  watch(() => currentMail.value , (value) => {
    if(value){
      attachFiles.value = []
      currentMail.value.attachments?.filter((item, index) => {
        attachFiles.value.push({
          name: item.name,
          url: item.url,
          type: item.content_type,
          id: index + 1
        })
      })
    }
  }, { immediate: true, deep : true })

  function makeAction(action, params = null|{}) {
    emits('handleAction', {action, params})
  }


  function buildMailHtml(htmlContent) {
    return `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          html, body {
            margin: 0;
            padding: 1rem;
            font-family: sans-serif;
          }

          figure.image {
            display: inline-block;
            max-width: 100%;
          }

          figure.image img {
            width: 100%;
            height: auto;
            display: block;
          }

          figure.image-style-align-right {
            float: right;
            margin-left: 1rem;
            margin-bottom: 1rem;
          }

          figure.image-style-align-left {
            float: left;
            margin-right: 1rem;
            margin-bottom: 1rem;
          }

          figure.image-style-align-center {
            margin: 0 auto;
            display: table;
          }
        </style>
      </head>
      <body>${htmlContent}</body>
    </html>
  `;
  }
  function downloadAllFile() {
    let data = {
      folder_path: folder.value,
      message_uid: props.detailItem.id
    }
    showSwal(
        '',
        t('mail.swal.download_all'),
        'info',
        "rgba(0, 114, 206, 1)"
      ).then( async (result) => {
        if (result.value) {
          disabled.value = true
          store.dispatch('mails/downloadAllFile', data)
            .then(res => {
              const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/zip' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;

              let filename = props.detailItem?.subject + '.zip'
              link.setAttribute('download', filename);

              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              window.toastr['success'](t('notification.download_successfully'))
            }).finally(() => disabled.value = false)
        }
    });
  }

  onMounted(() => {
    window.scrollTo(0, document.body.scrollHeight);
  })
</script>

<template>
  <div class="px-8">
    <div v-if="!isDetailLoading" class="bg-light my-5 p-5" style="border-radius: 5px;">
      <div class="d-flex justify-content-between">
        <div>
          <h3 v-if="!large">{{currentMail.subject}}</h3>
          <div class="row mb-2">
            <span class="fs-6 col-12 col-md-2 col-lg-2">{{$t('mail.of')}} : </span>
            <span v-if="currentMail.from_name && currentMail.from" class="fs-5 col-12 col-md-10 col-lg-10">{{currentMail.from_name + ', ' + currentMail.from}}</span>
            <span v-else-if="currentMail.from_name && !currentMail.from" class="fs-5 col-12 col-md-10 col-lg-10">{{currentMail.from_name}}</span>
            <span v-else-if="!currentMail.from_name && currentMail.from" class="fs-5 col-12 col-md-10 col-lg-10">{{currentMail.from?.personal + ', ' + currentMail.from?.mail}}</span>
          </div>
          <div class="d-flex" v-if="currentMail.to?.length > 0">
            <span class="fs-6 col-12 col-md-2 col-lg-2">{{$t('mail.to')}} : </span>
            <span class="fs-5 col-12 col-md-10 col-lg-10">
              <span class="badge badge-light-primary me-2" v-for="(to, index) in currentMail.to" :key="index">{{ to.full ? to.full : to.mail }}</span>
            </span>
          </div>
          <div class="d-flex">
            <span class="fs-6 col-12 col-md-2 col-lg-2">{{$t('mail.date')}} : </span>
            <span class="fs-5 col-12 col-md-10 col-lg-10">{{$dateFormatterWithDay(currentMail.date, locale, true)}}</span>
          </div>
          <div class="d-flex" v-if="currentMail.cc?.length > 0">
            <span class="fs-6 col-12 col-md-2 col-lg-2">{{$t('mail.cc')}} : </span>
            <span class="fs-5 col-12 col-md-10 col-lg-10">
              <span class="badge badge-light-primary me-2" v-for="(cc, index) in currentMail.cc" :key="index">{{ cc.full ? cc.full : cc.mail }}</span>
            </span>
          </div>
          <div class="d-flex" v-if="currentMail.bcc?.length > 0">
            <span class="fs-6 col-12 col-md-2 col-lg-2">CCi : </span>
            <span class="fs-5 col-12 col-md-10 col-lg-10">
              <span class="badge badge-light-primary me-2" v-for="(bcc, index) in currentMail.bcc" :key="index">{{ bcc.full ? bcc.full : bcc.mail }}</span>
            </span>
          </div>
        </div>
        <div>
          <div class="d-flex">
            <div class="d-flex p-3 me-5" :style="{backgroundColor:'rgb(226, 230, 233)', borderRadius: '5px'}">
              <span v-if="draftFolder" class="me-5 text-warning"><i class="fas fa-exclamation-triangle text-warning fs-4 me-2"></i>{{$t('mail.draft_warning')}}</span>
              <i v-if="inboxFolder || sendFolder" class="fas fa-tag fs-4 me-5"  :title="$t('mail.tagProject')" @click.prevent="makeAction(mailActions.TAG_PROJECT)" style="color: #303D4E; cursor: pointer;"></i>
              <i class="fas fa-edit fs-4" :class="draftFolder ? 'me-5' : ''" v-if="schedule || draftFolder" :title="$t('button.edit')" @click.prevent="makeAction(mailActions.EDIT)" style="color: #303D4E; cursor: pointer;"></i>
              <i v-if="!schedule && !draftFolder" class="fa fa-reply me-5 fs-4" :title="$t('mail.ans')" @click.prevent="makeAction(mailActions.REPLAY)" style="color: #303D4E; cursor: pointer;"></i>
              <i v-if="!schedule && !draftFolder" class="fa fa-reply-all me-5 fs-4" :title="$t('mail.ans_all')" @click.prevent="makeAction(mailActions.REPLAY_ALL)" style="color: #303D4E; cursor: pointer;"></i>
              <i v-if="!schedule && !draftFolder" class="fas fa-arrow-right fs-4" :title="$t('mail.transfer')" @click.prevent="makeAction(mailActions.SHARE)" style="color: #303D4E; cursor: pointer;"></i>
            </div>
            <button class="btn btn-sm" type="button" style="background-color: #eef3f7; border-radius: 5px;" @click.prevent="makeAction(mailActions.PRINT)">
              {{$t('mail.print')}}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isDetailLoading" class="content d-flex flex-column flex-column-fluid mb-20" id="kt_content2">
      <div class="container-xxl">
        <div class="row loader" style="text-align: center">
          <div class="spinner">
          </div>
          <div>{{ $t('loading') }}</div>
        </div>
      </div>
    </div>

    <div class="row" v-else>
      <div :class="attachFiles.length > 0 ? 'col-12 col-md-8 col-lg-8' : 'col-12'">
        <div class="card border_style">
          <div v-if="currentMail.body_html">
            <iframe :srcdoc="buildMailHtml(currentMail.body_html)" style="width: 100%; border: none; height: 400px !important;"></iframe>
          </div>

          <div v-if="!currentMail.body_html && currentMail.body_text">
            <div style="width: 100%; border: none; height: 400px !important; white-space: pre-line;">
              {{ currentMail.body_text }}
            </div>
          </div>
        </div>
      </div>
      <div :class="attachFiles.length > 0 ? 'col-12 col-md-4 col-lg-4' : 'col-12'" v-if="attachFiles.length > 0">
        <div class="card border_style">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <h2>{{$t('pipelines.attach_files')}}</h2>
              <span class="badge badge-light-warning fs-2 mb-2 ms-3">{{attachFiles.length}}</span>
            </div>
            <div class="card-toolbar">
              <div class="d-flex align-items-center" style="color: #303D4E; cursor: pointer;" :style="disabled ? 'pointer-events: none; opacity: 0.5;' : ''"  @click="downloadAllFile">
                <i class="fas fa-download fs-2" style="color: #303D4E; cursor: pointer;":title="$t('button.download')"></i>
              </div>
            </div>
          </div>
          <div class="card-body p-5">
            <div style="overflow-y: auto; max-height: 280px !important;">
              <FileUploaded modalType="modal-fullscreen" :remove="false" :attach-files="attachFiles"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .spinner-overlay {
    background-color: rgba(255, 255, 255, 0.3);
    z-index: 999;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .spinner {
    border: 6px solid #f3f3f3;
    border-top: 5px solid var(--bs-primary);
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 2s linear infinite;
    margin-left: 48%;
    margin-top: 5%;
    margin-bottom: 10px;
  }
  .border_style {
    border: 2px solid var(--bs-light)
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>